/**
 * Reading a spreadsheet of requisitions into purchase requests.
 *
 * These mirror the backend's `/api/purchase-requests/imports` contract. The shape to understand
 * first is `ImportSessionResponse`: mapping and preview always travel together, because every
 * edit re-reads the file and changes the counts, and a screen showing a mapping beside a stale
 * preview would be showing two different files.
 */

export type ImportSessionStatus =
  | "ANALYZING"
  | "READY_TO_REVIEW"
  | "IMPORTING"
  | "COMPLETED"
  | "FAILED"
  | "DISCARDED"
  | "EXPIRED";

/** Where a mapped value lands. The full vocabulary the backend will accept. */
export type ImportTargetField =
  | "item.quantity"
  | "item.measurementUnit"
  | "item.description"
  | "item.brand"
  | "item.costCenter"
  | "item.budgetValue"
  | "item.budgetCurrency"
  | "item.neededFrom"
  | "item.neededTo"
  | "item.purpose"
  | "item.address"
  | "item.assignedBuyer"
  | "item.site"
  | "material.clientMaterialCode"
  | "material.tags"
  | "material.globalMaterialName"
  | "material.brand"
  | "material.partNumber"
  | "request.name"
  | "request.purchaseRequestCode"
  | "request.comments"
  | "ignore";

/**
 * What has to be matched against something the company owns before it can be used.
 *
 * Units and currencies are deliberately absent: both are lookups the backend does itself, not
 * choices, and offering them here would be a question with no answers to pick from. Categories
 * left for the same reason from the other direction — what a file files a line under is kept as a
 * tag, created on sight, so there is nothing to choose between.
 */
export type ImportResolveType = "USER" | "SITE";

export interface ColumnMappingDTO {
  source: string;
  target: ImportTargetField;
  transforms?: string[] | null;
  append?: boolean | null;
  defaultValue?: string | null;
  valueMap?: Record<string, string> | null;
  resolve?: ImportResolveType | null;
  confidence?: string | null;
  rationale?: string | null;
}

export interface ImportMappingDTO {
  version?: number | null;
  sheet?: string | null;
  headerRow?: number | null;
  firstDataRow?: number | null;
  columns: ColumnMappingDTO[];
  /** Source columns whose shared values make one request. Empty means one request for the file. */
  groupBy: string[];
  /** Source columns whose combined value names one line, for spotting repeats. */
  identityKey: string[];
  requestNameTemplate?: string | null;
  notes?: string | null;
}

/** Where one field's values came from, across the whole file. */
export interface FieldStats {
  target: ImportTargetField;
  mapped: number;
  defaulted: number;
  derived: number;
  nulled: number;
  invalid: number;
  /** How many of the mapped values changed type or shape on the way in. */
  coerced: number;
}

export interface TypeCoercion {
  column: string;
  target: ImportTargetField;
  fromType: string;
  toType: string;
  rowCount: number;
}

/** Named values a warning or row issue carries, for the translated copy to fill in. */
export type ImportMessageParams = Record<string, string | number | null>;

/**
 * Something about the mapping as a whole. `code` names the case and `params` fills its copy;
 * `message` is the backend's English wording, kept for codes this screen does not know yet.
 */
export interface MappingWarning {
  code?: string | null;
  subject: string | null;
  params?: ImportMessageParams | null;
  message: string;
}

/** Something about one row. Same contract as `MappingWarning`: translate by `code`, fall back to `message`. */
export interface RowIssue {
  rowNumber: number;
  column: string | null;
  target: ImportTargetField | null;
  severity: "WARNING" | "ERROR";
  code?: string | null;
  params?: ImportMessageParams | null;
  message: string;
}

/**
 * The items one buyer would receive: `userId` once matched to a platform user, `fileValue` (the
 * file's spelling) until then, both null for the items the file assigns to nobody.
 */
export interface BuyerShare {
  userId: string | null;
  fileValue: string | null;
  itemCount: number;
}

export interface DuplicateFinding {
  identityValue: string;
  rows: number[];
  importedAt: string | null;
}

/** A distinct value nobody has matched yet. One entry per value, not per row. */
export interface UnresolvedValue {
  column: string;
  target: ImportTargetField;
  resolveType: ImportResolveType | null;
  value: string;
  rowCount: number;
  sampleRows: number[];
}

export interface ImportPreview {
  sheet: string;
  sheetCount: number;
  rowsRead: number;
  rowsSkipped: number;
  /** Always one when there is anything to import: a file is one request. */
  requestCount: number;
  /** Requisitions the file holds, kept as group labels on the request's items. */
  groupCount: number;
  itemCount: number;
  fields: FieldStats[];
  coercions: TypeCoercion[];
  warnings: MappingWarning[];
  issues: RowIssue[];
  issuesTruncated: boolean;
  /** Repeats inside this file. These stop the import until they are dealt with. */
  inFileDuplicates: DuplicateFinding[];
  /** Lines imported before. These only warn; importing again is the buyer's call. */
  historyDuplicates: DuplicateFinding[];
  unresolvedValues: UnresolvedValue[];
  /** Items per buyer. Absent from a backend that does not count them yet. */
  buyers?: BuyerShare[];
  canConfirm: boolean;
}

export interface ImportFormatMatch {
  formatId: string;
  name: string;
  similarity: number;
  missingColumns: string[];
  /** What was said about that format's files — nearly always what would be said about this one. */
  userDescription: string | null;
}

export interface ImportSessionResponse {
  id: string;
  status: ImportSessionStatus;
  sourceName: string;
  sheetName: string | null;
  sheetNames: string[];
  userDescription: string | null;
  formatId: string | null;
  formatName: string | null;
  /** True when a saved format matched the headers and no model was asked. */
  usedSavedFormat: boolean;
  mapping: ImportMappingDTO | null;
  preview: ImportPreview | null;
  /** Carried back on confirm, so a mapping edited in another tab cannot be imported by mistake. */
  previewHash: string | null;
  nearMissFormats: ImportFormatMatch[];
  importJobId: string | null;
  errorMessage: string | null;
}

/** One thing a buyer can choose when a value could not be matched. */
export interface ImportOption {
  id: string;
  label: string;
}

/** Everything the resolution dropdowns need, in one call. */
export interface ImportOptionsResponse {
  users: ImportOption[];
  sites: ImportOption[];
}

export interface ImportFormatResponse {
  id: string;
  name: string;
  status: "PROVISIONAL" | "APPROVED";
  /** Which revision of this format this is. The first is 1. */
  versionNumber: number;
  /** Shared by every version of the same format. */
  rootId: string | null;
  /** What the buyer said this kind of file is, so they never have to say it again. */
  userDescription: string | null;
  useCount: number;
  lastUsedAt: string | null;
  createdBy: string | null;
}

/**
 * What the backend can tell about a file before anything has been started.
 *
 * Sent the moment a file is chosen. A `match` means the company has imported a file with exactly
 * these columns before, and everything about how to read it — including the description somebody
 * once wrote — is already saved: there is nothing left to ask.
 */
export interface ImportProbeResponse {
  sourceName: string;
  sheetName: string;
  sheetNames: string[];
  headers: string[];
  match: ImportFormatResponse | null;
  nearMatches: ImportFormatNearMatch[];
}

export interface ImportFormatNearMatch {
  formatId: string;
  name: string;
  versionNumber: number;
  /** How much of the header set they share, 0 to 1. */
  similarity: number;
  missingColumns: string[];
  /** What was said about that format's files — nearly always what would be said about this one. */
  userDescription: string | null;
}

export interface ImportJobResponse {
  id: string;
  status: "PROCESSING" | "COMPLETED" | "FAILED";
  itemsCount: number | null;
  requestsCount: number | null;
  materialsCreated: number | null;
  purchaseRequestId: string | null;
  purchaseRequestCode: string | null;
  errorMessage: string | null;
  sourceName: string | null;
}

/**
 * The request details typed in on the import screen — the create form's "Request details" section.
 *
 * Every field is optional: blank text leaves what the file says (a blank number is generated), and
 * the date and budget only fill the items the file left without one.
 */
export interface ImportRequestDetails {
  name: string | null;
  purchaseRequestCode: string | null;
  comments: string | null;
  /** `yyyy-MM-dd`; a single date is sent as a range starting and ending that day. */
  neededFrom: string | null;
  neededTo: string | null;
  budgetCurrency: string | null;
  budgetValue: number | null;
  /** Attachments already uploaded to storage. */
  files: Array<{ fileURL: string; name: string }>;
  /** Who gets the items the file assigns to nobody. */
  assignedBuyerId: string | null;
}

export interface ConfirmImportPayload {
  previewHash?: string | null;
  /** Lines the buyer chose not to import again, having been told they already have them. */
  skipIdentities?: string[];
  saveFormatAs?: string | null;
  details?: ImportRequestDetails | null;
}

/** Every target field, in the order the mapping table offers them. */
export const IMPORT_TARGET_FIELDS: ImportTargetField[] = [
  "item.description",
  "item.quantity",
  "item.measurementUnit",
  "item.brand",
  "item.costCenter",
  "item.budgetValue",
  "item.budgetCurrency",
  "item.neededFrom",
  "item.neededTo",
  "item.purpose",
  "item.address",
  "item.assignedBuyer",
  "item.site",
  "material.clientMaterialCode",
  "material.globalMaterialName",
  "material.tags",
  "material.brand",
  "material.partNumber",
  "request.name",
  "request.purchaseRequestCode",
  "request.comments",
  "ignore",
];
